import {
  getStoreCount,
  Store,
  StoreScopesModal,
  StoreScopesPreview,
  StoreScopesProps,
  useStoreMap,
} from 'components/StoreScopes';
import {
  stores2storeScopes as activityStores2storeScopes,
  Preview,
  storeScopes2stores,
} from '@knowledge/Activity/share';
import {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMemoizedFn, useMount, useUnmount } from 'ahooks';
import {
  getBootProcessing,
  getDefaultValueRule,
  postBootProcessing,
} from 'views/BootGuide/service';
import { useStepName } from 'views/BootGuide/hooks/useStepName';
import { useUnConfigBootStoreContext } from 'views/BootGuide/contexts/UnConfigBootStoreContext';
import { useNextStep } from 'views/BootGuide/hooks/useNextStep';
import useModal from 'hooks/useModal';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import { Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { regionMonetaryUnitMap } from 'services/config/region';
import { uniq } from 'lodash-es';
import { css } from '@emotion/css';
import posthog from '@leyan/lytics';
import useLLM from 'views/BootGuide/hooks/useLLM';
import { storeScopes2storeIds } from '@knowledge/knowledges/general/StoreGroup/share';

export type Prefix = string | number | (string | number)[];

export const getPrefixName = (name: string, prefix?: Prefix) =>
  prefix ? (Array.isArray(prefix) ? [...prefix, name] : [prefix, name]).join('.') : name;

export function stores2storeScopes(stores: Store[]) {
  return activityStores2storeScopes(
    stores.map(({ externalType, region, id }) => ({
      platform: externalType,
      region,
      storeId: id,
    })),
  );
}

export type UnConfigStore = Store & { storeType: 'LOCAL' | 'CROSS'; is3pf: boolean };

export function useAiModelPublicState() {
  const { t } = useTranslate();

  const stepName = useStepName();

  const nextStep = useNextStep();

  const unConfigStores = useUnConfigBootStoreContext();

  const stores = useMemo(
    () =>
      unConfigStores.map(
        ({ storeId, externalStoreId, ...others }) =>
          ({
            id: Number(storeId),
            externalId: externalStoreId,
            ...others,
          } as UnConfigStore),
      ),
    [unConfigStores],
  );
  const storeMap = useStoreMap({
    list: stores,
  });

  const { visible, show, hide, meta } = useModal<StoreScopesProps & { key: any }>();

  const storeScopesModal = meta && (
    <StoreScopesModal
      allowEmpty
      showFooterTips={false}
      visible={visible}
      onCancel={() => hide()}
      {...meta}
    />
  );

  const [defaultValueRuleLoading, setDefaultValueRuleLoading] = useState(true);
  const [defaultValueRule, setDefaultValueRule] = useState<BootGuide.AiModel.DefaultValueRule>();

  useMount(() => {
    getDefaultValueRule()
      .then(setDefaultValueRule)
      .catch(() => {})
      .finally(() => {
        setDefaultValueRuleLoading(false);
      });
  });

  return {
    t,
    stepName,
    stores,
    storeMap,
    goNextStep: nextStep,
    storeScopesModal,
    showStoreScopesModal: show,
    defaultValueRuleLoading,
    defaultValueRule,
  };
}

export type AiModelPublicState = ReturnType<typeof useAiModelPublicState>;

export function useAiModelState<P extends BootGuide.AiModel.FormValues.Basic>(
  publicState: AiModelPublicState,
) {
  const { stepName, stores: originalStores } = publicState;

  const LLM = useLLM();

  const [initializing, setInitializing] = useState(true);
  const [storeIdList, setStoreIdList] = useState<string[]>();
  const [step, setStep] = useState(0);
  const [savedContentsList, setSavedContentsList] = useState<P[][]>();
  const [editingValues, setEditingValues] = useState<P[] | P>();

  const updateStep = useMemoizedFn((nextStep?: any) =>
    setStep(typeof nextStep === 'number' ? nextStep : (step || 0) + 1),
  );

  const stores = useMemo(() => {
    if (!storeIdList) {
      return originalStores;
    }
    return originalStores.filter(({ id }) => storeIdList.includes(`${id}`));
  }, [storeIdList, originalStores]);

  useMount(() => {
    getBootProcessing(LLM)
      .then((cache) => {
        const basicCache = cache.find((_) => _.configType === 'AI_MODEL');

        if (!basicCache) {
          setInitializing(false);
          return;
        }

        setStoreIdList(basicCache.storeIdList);

        const { aiModelState } = (() => {
          try {
            return JSON.parse(basicCache.content) || {};
          } catch (_) {
            return {};
          }
        })();

        if (!aiModelState || !aiModelState[stepName]) {
          setInitializing(false);
          return;
        }

        setStep(aiModelState[stepName].step);
        setSavedContentsList(aiModelState[stepName].savedContentsList);
        setEditingValues(aiModelState[stepName].editingValues);
        setInitializing(false);
      })
      .catch(() => {
        setInitializing(false);
      });
  });

  const editingValuesRef = useRef<P[] | P>();
  const endRef = useRef(false);

  useUnmount(() => {
    setTimeout(() => {
      if (endRef.current) {
        return;
      }

      getBootProcessing(LLM)
        .then((cache) => {
          const basicCache = cache.find((_) => _.configType === 'AI_MODEL');

          if (!basicCache) {
            return;
          }

          const content = (() => {
            try {
              return JSON.parse(basicCache.content) || {};
            } catch (_) {
              return {};
            }
          })();

          return postBootProcessing({
            ...basicCache,
            configType: 'AI_MODEL',
            stepName: stepName,
            status: 'IN_PROGRESS',
            content: JSON.stringify({
              ...content,
              aiModelState: {
                ...content.aiModelState,
                [stepName]: {
                  step,
                  savedContentsList,
                  editingValues: editingValuesRef.current || editingValues,
                },
              },
            }),
          });
        })
        .catch(() => {});
    }, 1000);
  });

  const getRemainStores = useCallback(
    (values?: { storeScopes: General.StoreScope[] }[]) => {
      return stores.filter((store) => {
        return (
          !values?.length ||
          values.every(({ storeScopes }) => !storeScopesIncludeStore(storeScopes, store))
        );
      });
    },
    [stores],
  );

  const remainStores = useMemo(() => {
    if (!savedContentsList?.length) {
      return stores;
    }
    return getRemainStores(savedContentsList.flat());
  }, [savedContentsList, stores, getRemainStores]);

  return {
    initializing,
    step,
    setStep,
    updateStep,
    savedContentsList,
    setSavedContentsList,
    editingValues,
    setEditingValues,
    editingValuesRef,
    endRef,
    getRemainStores,
    remainStores,
    ...publicState,
    stores,
  };
}

export type AiModelState<P extends BootGuide.AiModel.FormValues.Basic> = Omit<
  ReturnType<typeof useAiModelState>,
  | 'savedContentsList'
  | 'setSavedContentsList'
  | 'editingValues'
  | 'setEditingValues'
  | 'editingValuesRef'
> & {
  savedContentsList: P[][] | undefined;
  setSavedContentsList: Dispatch<SetStateAction<P[][] | undefined>>;
  editingValues: P[] | P | undefined;
  setEditingValues: Dispatch<SetStateAction<P[] | P | undefined>>;
  editingValuesRef: MutableRefObject<P[] | P | undefined>;
};

export function storeScopesIncludeStore(storeScopes: General.StoreScope[], store: Store) {
  return storeScopes.some((storeScope) => {
    return (
      storeScope.platform === store.externalType &&
      storeScope.regionStoreScopes.some((regionScope) => {
        return regionScope.region === store.region && regionScope.storeIds?.includes(store.id);
      })
    );
  });
}

export function uniqStoreScopes<P extends { storeScopes: General.StoreScope[] }>(
  value: General.StoreScope[],
  values: P[],
  index: number,
) {
  return values.map(({ storeScopes, ...others }, i) => {
    if (i === index) {
      return {
        ...others,
        storeScopes: value,
      } as P;
    }
    return {
      ...others,
      storeScopes: storeScopes
        .map((storeScope) => {
          const platformTarget = value.find((_) => _.platform === storeScope.platform);
          if (!platformTarget) {
            return storeScope;
          }
          return {
            ...storeScope,
            regionStoreScopes: storeScope.regionStoreScopes
              .map((regionScope) => {
                const regionTarget = platformTarget.regionStoreScopes.find(
                  (_) => _.region === regionScope.region,
                );
                if (!regionTarget) {
                  return regionScope;
                }
                return {
                  ...regionScope,
                  storeIds: regionScope.storeIds?.filter(
                    (_) => !regionTarget.storeIds?.includes(_),
                  ),
                };
              })
              .filter(({ storeIds }) => storeIds?.length),
          };
        })
        .filter(({ regionStoreScopes }) => regionStoreScopes.length),
    } as P;
  });
}

export function removeStoreScopesAll(storeScopes: General.StoreScope[], stores: Store[]) {
  return activityStores2storeScopes(storeScopes2stores(storeScopes, stores));
}

export function combineStoreScopes(storeScopesList: General.StoreScope[][]) {
  return storeScopesList.reduce<General.StoreScope[]>((res, storeScopes) => {
    return storeScopes.reduce((res, platformScopes) => {
      const platformTarget = res.find((target) => target.platform === platformScopes.platform);
      if (!platformTarget) {
        res.push({ ...platformScopes });
      } else {
        platformTarget.storeCount += platformScopes.storeCount;
        platformTarget.regionStoreScopes = [
          ...platformTarget.regionStoreScopes,
          ...platformScopes.regionStoreScopes,
        ].reduce<typeof platformScopes.regionStoreScopes>((res, regionScopes) => {
          const regionTarget = res.find((target) => target.region === regionScopes.region);
          if (!regionTarget) {
            res.push({ ...regionScopes });
          } else {
            regionTarget.storeCount += regionScopes.storeCount;
            regionTarget.storeIds = uniq([
              ...(regionTarget.storeIds || []),
              ...(regionScopes.storeIds || []),
            ]);
          }
          return res;
        }, []);
      }
      return res;
    }, res);
  }, []);
}

export function getStoreScopesReducedStoreIds(
  preStoreScopes: General.StoreScope[],
  nextStoreScopes: General.StoreScope[],
) {
  return preStoreScopes.reduce<number[]>((res, platformScopes) => {
    const platformTarget = nextStoreScopes.find(
      ({ platform }) => platform === platformScopes.platform,
    );
    if (!platformTarget) {
      res.push(...storeScopes2storeIds([platformScopes]));
      return res;
    }
    return platformScopes.regionStoreScopes.reduce((res, regionScopes) => {
      const regionTarget = platformTarget.regionStoreScopes.find(
        ({ region }) => region === regionScopes.region,
      );
      if (!regionTarget) {
        res.push(...(regionScopes.storeIds || []));
        return res;
      }
      return (regionScopes.storeIds || []).reduce((res, storeId) => {
        if (!regionTarget.storeIds?.includes(storeId)) {
          res.push(storeId);
        }
        return res;
      }, res);
    }, res);
  }, []);
}

export function storeScopesRemoveStoreIds(
  storeScopes: General.StoreScope[],
  removeStoreIds: number[],
) {
  return storeScopes
    .map(({ regionStoreScopes, ...others }) => {
      return {
        ...others,
        regionStoreScopes: regionStoreScopes
          .map(({ storeIds, ...others }) => {
            return {
              ...others,
              storeIds: storeIds?.filter((id) => !removeStoreIds.includes(id)),
            };
          })
          .filter(({ storeIds }) => storeIds?.length),
      };
    })
    .filter(({ regionStoreScopes }) => regionStoreScopes.length);
}

export function StoreSelect({
  isAll,
  value,
  storeMap,
  onEdit,
  descriptionRender,
}: {
  isAll?: boolean;
  value: General.StoreScope[];
  storeMap: AiModelPublicState['storeMap'];
  onEdit?: () => void;
  descriptionRender?: (
    value: General.StoreScope[],
    storeMap: AiModelPublicState['storeMap'],
  ) => ReactNode;
}) {
  const { t } = useTranslate();
  return (
    <Inline>
      <span>
        {isAll
          ? `${t('department.全部店铺')}(${t('x家', { count: getStoreCount(value) })})`
          : t('ViewBlockList.{x}家店铺', { count: getStoreCount(value) })}
      </span>
      {descriptionRender && descriptionRender(value, storeMap)}
      <Preview
        content={
          <PreviewWrapper>
            <StoreScopesPreview
              value={value}
              showSearch={false}
              showTitle={false}
              storeMap={storeMap}
            />
          </PreviewWrapper>
        }
      />
      {onEdit && <Button type="text" icon={<EditOutlined />} onClick={onEdit} />}
    </Inline>
  );
}

export const Inline = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > * {
    margin: 0 4px;
    &:first-child {
      margin-left: 0;
    }
  }
  .ant-input-number {
    width: 60px;
  }
  .ant-picker {
    width: 80px;
  }
  > .ant-select {
    width: auto;
  }
`;

export const Footer = styled.footer`
  padding: 12px 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  > div {
    margin-right: 12px;
    font-size: 12px;
    color: var(--fett-text-secondary);
  }
`;

export const hideRadioClassName = css`
  > span {
    padding-left: 0;
  }
  > .ant-radio {
    display: none;
  }
`;

export const defaultValueMap: BootGuide.AiModel.FormValues.Map = {
  AI_MODEL_SHIPPING_DAYS: {
    storeScopes: [],
    valueType: 'asSoon',
    saleType: 'all',
    todayOrTomorrowTime: '16:00',
    range: {
      rangeMin: 2,
      rangeMax: 3,
      unit: 'DAY',
    },
    underMax: {
      max: 3,
      unit: 'DAY',
    },
  },
  AI_MODEL_SHIP_PLACE: {
    storeScopes: [],
    valueType: 'nearBy',
  },
  AI_MODEL_TRANSIT_DAYS: {
    storeScopes: [],
    valueType: 'asSoon',
    range: {
      min: 3,
      max: 10,
    },
    areas: [
      {
        areas: [],
        rangeMin: 3,
        rangeMax: 10,
      },
    ],
    transports: [
      {
        deliveryMethods: [],
        rangeMin: 3,
        rangeMax: 10,
      },
    ],
  },
  AI_MODEL_PHOTOS_BEFORE_SHIPPING: {
    value: 'noSupport',
    storeScopes: [],
  },
  AI_MODEL_SPECIFY_EXPRESS_TYPE: {
    value: 'noSupport',
    storeScopes: [],
    deliveryTypes: [],
  },
  AI_MODEL_COMBINED_SHIPPING: {
    value: 'noSupport',
    storeScopes: [],
  },
  AI_MODEL_FREE_SHIPPING: {
    freeShippingType: 'NO_FREE_SHIPPING',
    storeScopes: [],
  },
  AI_MODEL_PROVIDE_INVOICE: {
    value: 'receipt',
    storeScopes: [],
  },
  AI_MODEL_DROPSSHIPPING: {
    value: 'noSupport',
    storeScopes: [],
    dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
  },
  AI_MODEL_PICK_UP: {
    value: 'noSupport',
    storeScopes: [],
  },
  AI_MODEL_SERVICE_WORKING_TIME: {
    storeScopes: [],
    weekRepeat: [
      {
        dayOfWeeks: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
        timeRanges: [
          {
            startAt: '09:00',
            endAt: '18:00',
          },
        ],
      },
    ],
  },
  AI_MODEL_WARRANTY: {
    value: 'noSupport',
    storeScopes: [],
    number: 1,
    unit: 'YEAR',
  },
  AI_MODEL_SHELF_LIFE: {
    value: 'noSupport',
    storeScopes: [],
    number: 1,
    unit: 'YEAR',
  },
  AI_MODEL_PRIVATE_SHIPPING: {
    value: 'noSupport',
    storeScopes: [],
    items: [],
  },
  AI_MODEL_GIFT: {
    storeScopes: [],
    value: 'NO_GIFT',
    number: 2,
    currencyAmountList: [],
    specifiedItems: [],
  },
  AI_MODEL_WHOLESALE: {
    value: 'noSupport',
    storeScopes: [],
  },
};

const knowledge2formValueMapWithoutCatch: {
  [T in Knowledge.KnowledgeType]: (
    storeScopes: General.StoreScope[],
    knowledge: { templateCode: string; value: string; saleTypes?: ('ONSALE' | 'PREORDER')[] },
  ) => Knowledge.KnowledgeFormValueMap[T] | undefined;
} = {
  LOGISTICS_SEND_OUT_TIME: (storeScopes, { templateCode, value, saleTypes = [] }) => {
    const saleType =
      saleTypes.includes('ONSALE') && saleTypes.includes('PREORDER')
        ? 'all'
        : saleTypes.includes('ONSALE')
        ? 'inSale'
        : 'preSale';
    if (templateCode === 'AS_SOON') {
      return {
        ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
        storeScopes,
        valueType: 'asSoon',
        saleType,
      };
    }
    if (templateCode === 'TODAY_OR_TOMORROW_ON_TIME') {
      const { today_or_tomorrow_time, todayOrTomorrowTime = today_or_tomorrow_time } =
        JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
        storeScopes,
        valueType: 'time',
        todayOrTomorrowTime,
        saleType,
      };
    }
    if (templateCode === 'RANGE') {
      const { rangeMin, rangeMax, unit } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
        storeScopes,
        valueType: 'range',
        range: {
          rangeMin,
          rangeMax,
          unit,
        },
        saleType,
      };
    }
    if (templateCode === 'UNDER_MAX') {
      const { max, unit } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
        storeScopes,
        valueType: 'underMax',
        underMax: {
          max,
          unit,
        },
        saleType,
      };
    }
    return undefined;
  },
  LOGISTICS_DELIVERED_IN_TIME: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'DELIVERED_RANGE') {
      const { rangeMin, rangeMax } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_TRANSIT_DAYS,
        valueType: 'range',
        storeScopes,
        range: {
          min: rangeMin,
          max: rangeMax,
        },
      };
    }
    if (templateCode === 'DELIVERED_RANGE_OF_AREA') {
      const { areaRanges } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_TRANSIT_DAYS,
        valueType: 'area',
        storeScopes,
        areas: areaRanges.map(({ areas, rangeMin, rangeMax }: any) => {
          return {
            areas,
            rangeMin,
            rangeMax,
          };
        }),
      };
    }
    if (templateCode === 'TRANSPORT_DELIVERY_METHOD') {
      const { deliveryMethodRanges } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_TRANSIT_DAYS,
        valueType: 'transport',
        storeScopes,
        transports: deliveryMethodRanges.map(({ deliveryMethods, rangeMin, rangeMax }: any) => {
          return {
            deliveryMethods,
            rangeMin,
            rangeMax,
          };
        }),
      };
    }
    if (templateCode === 'DELIVERED_NO_GUARANTEE') {
      return {
        ...defaultValueMap.AI_MODEL_TRANSIT_DAYS,
        storeScopes,
        valueType: 'asSoon',
      };
    }
    return undefined;
  },
  LOGISTICS_SEND_OUT_PLACE: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'SHIP_NEARBY') {
      return {
        storeScopes,
        valueType: 'nearBy',
        areas: [],
      };
    }
    if (templateCode === 'SEND_OUT_PLACE_OF_AREA') {
      const { areas } = JSON.parse(value);
      return {
        storeScopes,
        valueType: 'area',
        areas: areas,
      };
    }
  },
  LOGISTICS_SEND_PICTURES_BEFORE: (storeScopes, { templateCode }) => {
    if (templateCode === 'SUPPORT') {
      return {
        storeScopes,
        value: 'support',
      };
    }
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
      };
    }
    if (templateCode === 'UNCERTAIN') {
      return {
        storeScopes,
        value: 'uncertain',
      };
    }
    return undefined;
  },
  LOGISTICS_DELIVERY_COURIER_TYPE: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
        deliveryTypes: [],
      };
    }
    if (templateCode === 'SUPPORT') {
      const { deliveryTypes } = JSON.parse(value);
      return {
        storeScopes,
        value: 'support',
        deliveryTypes,
      };
    }
    return undefined;
  },
  LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: (storeScopes, { templateCode }) => {
    if (templateCode === 'SUPPORT') {
      return {
        storeScopes,
        value: 'support',
      };
    }
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
      };
    }
    if (templateCode === 'UNCERTAIN') {
      return {
        storeScopes,
        value: 'uncertain',
      };
    }
    return undefined;
  },
  SHOP_FREE_SHIPPING: (storeScopes, { templateCode }) => {
    if (templateCode === 'FREE_SHIPPING') {
      return {
        storeScopes,
        freeShippingType: 'FREE_SHIPPING',
      };
    }
    if (templateCode === 'NO_FREE_SHIPPING') {
      return {
        storeScopes,
        freeShippingType: 'NO_FREE_SHIPPING',
      };
    }
    if (templateCode === 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON') {
      return {
        storeScopes,
        freeShippingType: 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
      };
    }
    return undefined;
  },
  SHOP_INVOICE: (storeScopes, { templateCode }) => {
    if (templateCode === 'PROVIDE_RECEIPTS') {
      return {
        storeScopes,
        value: 'receipt',
      };
    }
    if (templateCode === 'PROVIDE_INVOICES') {
      return {
        storeScopes,
        value: 'invoice',
      };
    }
    if (templateCode === 'PROVIDE_INVOICES_AND_RECEIPTS') {
      return {
        storeScopes,
        value: 'invoiceAndReceipt',
      };
    }
    if (templateCode === 'NO_PROVIDE_INVOICES_AND_RECEIPTS') {
      return {
        storeScopes,
        value: 'none',
      };
    }
    return undefined;
  },
  SHOP_DROPSHIPPING: (storeScopes, { templateCode }) => {
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
        dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
      };
    }
    if (templateCode === 'SYSTEM_PROVIDES_TRACKING_NUMBER') {
      return {
        storeScopes,
        value: 'support',
        dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
      };
    }
    if (templateCode === 'SYSTEM_NOT_PROVIDES_TRACKING_NUMBER') {
      return {
        storeScopes,
        value: 'support',
        dropShippingType: 'SYSTEM_NOT_PROVIDES_TRACKING_NUMBER',
      };
    }
    return undefined;
  },
  SHOP_PICKUP_AT_HOME: (storeScopes, { templateCode }) => {
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
      };
    }
    if (templateCode === 'SUPPORT') {
      return {
        storeScopes,
        value: 'support',
      };
    }
    return undefined;
  },
  SHOP_CUSTOMER_SERVICE_WORKING_TIME: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'CUSTOMER_SERVICE_WORKING_TIME') {
      return {
        storeScopes,
        ...JSON.parse(value),
      };
    }
    return undefined;
  },
  ITEM_WARRANTY_SERVICE: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'NO_WARRANTY_SERVICE') {
      return {
        storeScopes,
        value: 'noSupport',
        number: 1,
        unit: 'YEAR',
      };
    }
    if (templateCode === 'WARRANTY_SERVICE') {
      const { number, unit } = JSON.parse(value);
      return {
        storeScopes,
        value: 'support',
        number,
        unit,
      };
    }
    return undefined;
  },
  ITEM_EXPIRATION_DATE: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'NO_EXPIRATION_DATE') {
      return {
        storeScopes,
        value: 'noSupport',
        number: 1,
        unit: 'YEAR',
      };
    }
    if (templateCode === 'EXPIRATION_DATE') {
      const { number, unit } = JSON.parse(value);
      return {
        storeScopes,
        value: 'support',
        number,
        unit,
      };
    }
    return undefined;
  },
  OTHER_PRIVATE_SHIPPING: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
        items: [],
      };
    }
    if (templateCode === 'SUPPORT') {
      return {
        storeScopes,
        value: 'support',
        ...JSON.parse(value),
      };
    }
    return undefined;
  },
  OTHER_GIFT: (storeScopes, { templateCode, value }) => {
    if (templateCode === 'NO_GIFT') {
      return {
        ...defaultValueMap.AI_MODEL_GIFT,
        storeScopes,
        value: 'NO_GIFT',
      };
    }
    if (templateCode === 'ALL_GIFT') {
      return {
        ...defaultValueMap.AI_MODEL_GIFT,
        storeScopes,
        value: 'ALL_GIFT',
      };
    }
    if (templateCode === 'GIFT_BY_NUM') {
      const { number } = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_GIFT,
        storeScopes,
        value: 'GIFT_BY_NUM',
        number,
      };
    }
    if (templateCode === 'GIFT_BY_AMOUNT') {
      const currencyAmountList = JSON.parse(value);
      return {
        ...defaultValueMap.AI_MODEL_GIFT,
        storeScopes,
        value: 'GIFT_BY_AMOUNT',
        currencyAmountList: currencyAmountList.map(({ currency, ...others }: any) => {
          return {
            ...others,
            currency,
            region: Object.keys(regionMonetaryUnitMap)
              .find((key) => regionMonetaryUnitMap[key] === currency)
              ?.toUpperCase(),
          };
        }),
      };
    }
    if (templateCode === 'GIFT_BY_SPU') {
      return {
        ...defaultValueMap.AI_MODEL_GIFT,
        storeScopes,
        ...JSON.parse(value),
        value: 'GIFT_BY_SPU',
      };
    }
    return undefined;
  },
  SHOP_WHOLESALE: (storeScopes, { templateCode }) => {
    if (templateCode === 'NOT_SUPPORT') {
      return {
        storeScopes,
        value: 'noSupport',
      };
    }
    if (templateCode === 'SUPPORT') {
      return {
        storeScopes,
        value: 'support',
      };
    }
    return undefined;
  },
};

export const knowledge2formValueMap = Object.keys(knowledge2formValueMapWithoutCatch).reduce(
  (res, key) => {
    res[key] = (...props: any) => {
      try {
        const res = (knowledge2formValueMapWithoutCatch[key] as any)(...props);
        return res;
      } catch (e: any) {
        posthog.capture('$parseKnowledgeError', {
          e,
          error: e?.message,
        });
        return undefined;
      }
    };
    return res;
  },
  {} as typeof knowledge2formValueMapWithoutCatch,
);

export function knowledgeValueMap2formValueMap(knowledgeValueMap: any) {
  const knowledges2formValues = (knowledgeType: Knowledge.KnowledgeType) => {
    return knowledgeValueMap[knowledgeType].map(
      ({ itemScope: { stores, saleTypes }, templateCode, value }: any) => {
        return knowledge2formValueMap[knowledgeType](activityStores2storeScopes(stores), {
          templateCode,
          value,
          saleTypes,
        });
      },
    );
  };

  return {
    AI_MODEL_SHIPPING_DAYS: knowledges2formValues('LOGISTICS_SEND_OUT_TIME').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.ShippingDays[],
    AI_MODEL_TRANSIT_DAYS: knowledges2formValues('LOGISTICS_DELIVERED_IN_TIME').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.TransitDays[],
    AI_MODEL_PHOTOS_BEFORE_SHIPPING: knowledges2formValues('LOGISTICS_SEND_PICTURES_BEFORE').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.SendPhotoBeforeShipping[],
    AI_MODEL_SPECIFY_EXPRESS_TYPE: knowledges2formValues('LOGISTICS_DELIVERY_COURIER_TYPE').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.SpecifyExpressType[],
    AI_MODEL_COMBINED_SHIPPING: knowledges2formValues(
      'LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
    ).filter((_: any) => _) as BootGuide.AiModel.FormValues.CombinedShipping[],
    AI_MODEL_FREE_SHIPPING: knowledges2formValues('SHOP_FREE_SHIPPING').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.FreeShipping[],
    AI_MODEL_PROVIDE_INVOICE: knowledges2formValues('SHOP_INVOICE').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.ProvideInvoice[],
    AI_MODEL_DROPSSHIPPING: knowledges2formValues('SHOP_DROPSHIPPING').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.DropsShipping[],
    AI_MODEL_PICK_UP: knowledges2formValues('SHOP_PICKUP_AT_HOME').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.PickUp[],
    AI_MODEL_SERVICE_WORKING_TIME: knowledges2formValues(
      'SHOP_CUSTOMER_SERVICE_WORKING_TIME',
    ).filter((_: any) => _) as BootGuide.AiModel.FormValues.ServiceWorkingTime[],
    AI_MODEL_WARRANTY: knowledges2formValues('ITEM_WARRANTY_SERVICE').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.Warranty[],
    AI_MODEL_SHELF_LIFE: knowledges2formValues('ITEM_EXPIRATION_DATE').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.ShelfLife[],
    AI_MODEL_PRIVATE_SHIPPING: knowledges2formValues('OTHER_PRIVATE_SHIPPING').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.PrivateShipping[],
    AI_MODEL_GIFT: knowledges2formValues('OTHER_GIFT').filter(
      (_: any) => _,
    ) as BootGuide.AiModel.FormValues.Gift[],
  };
}

export const formValue2knowledgeMap: Record<
  Knowledge.KnowledgeType,
  (formValue: any) => undefined | { templateCode: string; content: string }
> = {
  LOGISTICS_SEND_OUT_TIME: (formValue) => {
    const { valueType, saleType, todayOrTomorrowTime, range, underMax } = formValue;
    const itemScope = {
      filterCode: 'ITEM_ALL',
      saleTypes:
        saleType === 'all'
          ? ['ONSALE', 'PREORDER']
          : saleType === 'inSale'
          ? ['ONSALE']
          : ['PREORDER'],
    };
    if (valueType === 'asSoon') {
      return {
        itemScope,
        templateCode: 'AS_SOON',
        content: JSON.stringify({}),
      };
    } else if (valueType === 'time') {
      return {
        itemScope,
        templateCode: 'TODAY_OR_TOMORROW_ON_TIME',
        content: JSON.stringify({ today_or_tomorrow_time: todayOrTomorrowTime }),
      };
    } else if (valueType === 'range') {
      return {
        itemScope,
        templateCode: 'RANGE',
        content: JSON.stringify(range),
      };
    } else if (valueType === 'underMax') {
      return {
        itemScope,
        templateCode: 'UNDER_MAX',
        content: JSON.stringify(underMax),
      };
    }
  },
  LOGISTICS_SEND_OUT_PLACE: (formValue) => {
    const { valueType, areas } = formValue;
    if (valueType === 'nearBy') {
      return {
        templateCode: 'SHIP_NEARBY',
        content: JSON.stringify({}),
      };
    } else if (valueType === 'area') {
      return {
        templateCode: 'SEND_OUT_PLACE_OF_AREA',
        content: JSON.stringify({
          areas,
        }),
      };
    }
  },
  LOGISTICS_DELIVERED_IN_TIME: (formValue) => {
    const { valueType, range, areas, transports } = formValue;
    if (valueType === 'asSoon') {
      return {
        templateCode: 'DELIVERED_NO_GUARANTEE',
        content: JSON.stringify({}),
      };
    } else if (valueType === 'range') {
      return {
        templateCode: 'DELIVERED_RANGE',
        content: JSON.stringify({
          rangeMin: range.min,
          rangeMax: range.max,
        }),
      };
    } else if (valueType === 'area') {
      return {
        templateCode: 'DELIVERED_RANGE_OF_AREA',
        content: JSON.stringify({
          areaRanges: areas,
        }),
      };
    } else if (valueType === 'transport') {
      return {
        templateCode: 'TRANSPORT_DELIVERY_METHOD',
        content: JSON.stringify({
          deliveryMethodRanges: transports,
        }),
      };
    }
  },
  LOGISTICS_SEND_PICTURES_BEFORE: (formValue) => {
    const { value } = formValue;
    if (value) {
      return {
        templateCode: {
          support: 'SUPPORT',
          noSupport: 'NOT_SUPPORT',
          uncertain: 'UNCERTAIN',
        }[value as string] as string,
        content: JSON.stringify({}),
      };
    }
  },
  LOGISTICS_DELIVERY_COURIER_TYPE: (formValue) => {
    const { value, deliveryTypes } = formValue;
    if (value === 'noSupport') {
      return {
        templateCode: 'NOT_SUPPORT',
        content: JSON.stringify({}),
      };
    } else if (value === 'support') {
      return {
        templateCode: 'SUPPORT',
        content: JSON.stringify({ deliveryTypes }),
      };
    }
  },
  LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: (formValue) => {
    const { value } = formValue;
    if (value) {
      return {
        templateCode: {
          support: 'SUPPORT',
          noSupport: 'NOT_SUPPORT',
          uncertain: 'UNCERTAIN',
        }[value as string] as string,
        content: JSON.stringify({}),
      };
    }
  },
  SHOP_FREE_SHIPPING: (formValue) => {
    const { freeShippingType } = formValue;
    if (freeShippingType) {
      return {
        templateCode: freeShippingType,
        content: JSON.stringify({}),
      };
    }
  },
  SHOP_INVOICE: (formValue) => {
    const { value } = formValue;
    if (value) {
      return {
        templateCode: {
          receipt: 'PROVIDE_RECEIPTS',
          invoice: 'PROVIDE_INVOICES',
          invoiceAndReceipt: 'PROVIDE_INVOICES_AND_RECEIPTS',
          none: 'NO_PROVIDE_INVOICES_AND_RECEIPTS',
        }[value as string] as string,
        content: JSON.stringify({}),
      };
    }
  },
  SHOP_DROPSHIPPING: (formValue) => {
    const { value, dropShippingType } = formValue;
    if (value) {
      return {
        templateCode: value === 'noSupport' ? 'NOT_SUPPORT' : dropShippingType,
        content: JSON.stringify({}),
      };
    }
  },
  SHOP_PICKUP_AT_HOME: (formValue) => {
    const { value } = formValue;
    if (value) {
      return {
        templateCode: value === 'noSupport' ? 'NOT_SUPPORT' : 'SUPPORT',
        content: JSON.stringify({}),
      };
    }
  },
  SHOP_CUSTOMER_SERVICE_WORKING_TIME: (formValue) => {
    const { weekRepeat } = formValue;
    if (weekRepeat?.length) {
      return {
        templateCode: 'CUSTOMER_SERVICE_WORKING_TIME',
        content: JSON.stringify({ weekRepeat }),
      };
    }
  },
  ITEM_WARRANTY_SERVICE: (formValue) => {
    const { value, number, unit } = formValue;
    if (value === 'noSupport') {
      return {
        templateCode: 'NO_WARRANTY_SERVICE',
        content: JSON.stringify({}),
      };
    } else if (value === 'support') {
      return {
        templateCode: 'WARRANTY_SERVICE',
        content: JSON.stringify({ number, unit }),
      };
    }
  },
  ITEM_EXPIRATION_DATE: (formValue) => {
    const { value, number, unit } = formValue;
    if (value === 'noSupport') {
      return {
        templateCode: 'NO_EXPIRATION_DATE',
        content: JSON.stringify({}),
      };
    } else if (value === 'support') {
      return {
        templateCode: 'EXPIRATION_DATE',
        content: JSON.stringify({ number, unit }),
      };
    }
  },
  OTHER_PRIVATE_SHIPPING: (formValue) => {
    const { value, items } = formValue;
    if (value === 'noSupport') {
      return {
        templateCode: 'NOT_SUPPORT',
        content: JSON.stringify({}),
      };
    } else if (value === 'support') {
      return {
        templateCode: 'SUPPORT',
        content: JSON.stringify({ items }),
      };
    }
  },
  OTHER_GIFT: (formValue) => {
    const { value: giftType, number, currencyAmountList, specifiedItems } = formValue;
    if (giftType) {
      return {
        templateCode: giftType,
        content: JSON.stringify(
          giftType === 'GIFT_BY_NUM'
            ? { number }
            : giftType === 'GIFT_BY_AMOUNT'
            ? currencyAmountList.map(({ region, ...others }: any) => others)
            : giftType === 'GIFT_BY_SPU'
            ? { specifiedItems }
            : {},
        ),
      };
    }
  },
  SHOP_WHOLESALE: (formValue) => {
    const { value } = formValue;
    if (value) {
      return {
        templateCode: value === 'noSupport' ? 'NOT_SUPPORT' : 'SUPPORT',
        content: JSON.stringify({}),
      };
    }
  },
};

export const stepKnowledgeTypeMap = {
  AI_MODEL_SHIPPING_DAYS: 'LOGISTICS_SEND_OUT_TIME',
  AI_MODEL_TRANSIT_DAYS: 'LOGISTICS_DELIVERED_IN_TIME',
  AI_MODEL_PHOTOS_BEFORE_SHIPPING: 'LOGISTICS_SEND_PICTURES_BEFORE',
  AI_MODEL_SPECIFY_EXPRESS_TYPE: 'LOGISTICS_DELIVERY_COURIER_TYPE',
  AI_MODEL_COMBINED_SHIPPING: 'LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
  AI_MODEL_FREE_SHIPPING: 'SHOP_FREE_SHIPPING',
  AI_MODEL_PROVIDE_INVOICE: 'SHOP_INVOICE',
  AI_MODEL_DROPSSHIPPING: 'SHOP_DROPSHIPPING',
  AI_MODEL_PICK_UP: 'SHOP_PICKUP_AT_HOME',
  AI_MODEL_SERVICE_WORKING_TIME: 'SHOP_CUSTOMER_SERVICE_WORKING_TIME',
  AI_MODEL_WARRANTY: 'ITEM_WARRANTY_SERVICE',
  AI_MODEL_SHELF_LIFE: 'ITEM_EXPIRATION_DATE',
  AI_MODEL_PRIVATE_SHIPPING: 'OTHER_PRIVATE_SHIPPING',
  AI_MODEL_GIFT: 'OTHER_GIFT',
  AI_MODEL_SHIP_PLACE: 'LOGISTICS_SEND_OUT_PLACE',
  AI_MODEL_WHOLESALE: 'SHOP_WHOLESALE',
} as const;

export function formValueMap2knowledges(formValueMap: any) {
  return Object.keys(formValueMap).reduce((res, key) => {
    const formValue = formValueMap[key];
    const knowledgeType = stepKnowledgeTypeMap[key as keyof typeof stepKnowledgeTypeMap];
    if (knowledgeType) {
      const knowledge = formValue2knowledgeMap[knowledgeType](formValue);
      if (knowledge) {
        res.push({
          definitionCode: knowledgeType,
          storeScope: {
            storeType: 'ALL',
            storeScope: formValue.storeScopes,
          },
          itemScope: {
            filterCode: 'ITEM_ALL',
            saleTypes: ['ONSALE', 'PREORDER'],
          },
          dtScope: {
            filterCode: 'DT_ALL',
          },
          tradeStatusScope: {
            filterCode: 'TRADE_STATUS_ALL',
          },
          ...knowledge,
        });
      }
    }
    return res;
  }, [] as any[]);
}

export const knowledgeTypeStepMap = {
  LOGISTICS_SEND_OUT_TIME: 'AI_MODEL_SHIPPING_DAYS',
  LOGISTICS_DELIVERED_IN_TIME: 'AI_MODEL_TRANSIT_DAYS',
  LOGISTICS_SEND_OUT_PLACE: 'AI_MODEL_SHIP_PLACE',
  LOGISTICS_SEND_PICTURES_BEFORE: 'AI_MODEL_PHOTOS_BEFORE_SHIPPING',
  LOGISTICS_DELIVERY_COURIER_TYPE: 'AI_MODEL_SPECIFY_EXPRESS_TYPE',
  LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: 'AI_MODEL_COMBINED_SHIPPING',
  SHOP_FREE_SHIPPING: 'AI_MODEL_FREE_SHIPPING',
  SHOP_INVOICE: 'AI_MODEL_PROVIDE_INVOICE',
  SHOP_DROPSHIPPING: 'AI_MODEL_DROPSSHIPPING',
  SHOP_PICKUP_AT_HOME: 'AI_MODEL_PICK_UP',
  SHOP_CUSTOMER_SERVICE_WORKING_TIME: 'AI_MODEL_SERVICE_WORKING_TIME',
  ITEM_WARRANTY_SERVICE: 'AI_MODEL_WARRANTY',
  ITEM_EXPIRATION_DATE: 'AI_MODEL_SHELF_LIFE',
  OTHER_PRIVATE_SHIPPING: 'AI_MODEL_PRIVATE_SHIPPING',
  OTHER_GIFT: 'AI_MODEL_GIFT',
  SHOP_WHOLESALE: 'AI_MODEL_WHOLESALE',
} as const;

export { activityStores2storeScopes };
